import type { TFunction } from 'i18next';
import { confirm, notice } from './confirm-action';
import { formatSkillUndoFields, readSkillUndoConflict } from './skill-undo-conflict';
import { undoSkillRun, type UndoSkillRunResult } from './skills-api';

export type SkillUndoOutcome =
  | { kind: 'undone'; result: UndoSkillRunResult }
  /** The user kept their edits (or an edited copy was kept): nothing changed. */
  | { kind: 'kept' };

/**
 * Undo one skill run without ever overwriting edits silently (CV spine B05).
 *
 * The first request never forces. When the API answers SKILL_UNDO_CONFLICT,
 * the user sees what the undo would replace and chooses "Restore anyway"
 * (a second request with `force: true`) or "Keep my edits" (nothing more is
 * sent). A copy that gained edits is never deleted: the app says it was kept.
 * Any other failure is rethrown for the caller's own error message.
 *
 * Native shows the RN Alert and web the in-app dialog (`confirm-action` has
 * a platform twin), never the browser's window.confirm.
 */
export async function undoSkillRunWithConflictPrompt(
  runId: string,
  t: TFunction,
): Promise<SkillUndoOutcome> {
  try {
    return { kind: 'undone', result: await undoSkillRun(runId) };
  } catch (error) {
    const conflict = readSkillUndoConflict(error);
    if (!conflict) throw error;

    if (!conflict.canRestoreAnyway) {
      const copy = conflict.reason === 'copy_edited';
      await notice({
        title: t(
          copy
            ? 'skills.undoConflict.copyTitle'
            : 'skills.undoConflict.unavailableTitle',
        ),
        message: t(
          copy
            ? 'skills.undoConflict.copyMessage'
            : 'skills.undoConflict.unavailableMessage',
        ),
        okLabel: t('skills.undoConflict.ok'),
      });
      return { kind: 'kept' };
    }

    const fields = formatSkillUndoFields(conflict.fields, t);
    const restore = await confirm({
      title: t('skills.undoConflict.title'),
      message:
        conflict.reason === 'unknown_base'
          ? t('skills.undoConflict.unknownMessage', { fields })
          : t('skills.undoConflict.message', { fields }),
      confirmLabel: t('skills.undoConflict.restoreAnyway'),
      cancelLabel: t('skills.undoConflict.keepEdits'),
      destructive: true,
    });
    if (!restore) return { kind: 'kept' };
    return {
      kind: 'undone',
      result: await undoSkillRun(runId, { force: true }),
    };
  }
}
